import { StyleSheet } from 'aphrodite';
import { THEME } from '../../components-v2/Theme/theme';
import { shadowSm } from '../../styles/shadows';

export const styleSheet = StyleSheet.create({
	accountCount: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		textAlign: 'right',
	},
	accountName: {
		...THEME.common.truncate,
		color: THEME.colors.text.main,
		flex: 1,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.2,
		margin: 0,
		minWidth: 0,
	},
	// The account name cell holds a full-bleed toggle Button, so the cell itself carries no
	// padding — the Button provides it, making the whole cell the clickable expand target.
	accountCell: {
		padding: 0,
	},
	accountToggle: {
		backgroundColor: 'transparent',
		border: 0,
		borderRadius: 0,
		boxShadow: 'none',
		boxSizing: 'border-box',
		color: THEME.colors.text.main,
		display: 'flex',
		gap: THEME.spacing.sm,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		textAlign: 'left',
		width: '100%',
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
	},
	chevron: {
		color: THEME.colors.icon.gray,
		flexShrink: 0,
		height: 18,
		transition: 'transform 0.2s ease',
		width: 18,
	},
	chevronExpanded: {
		transform: 'rotate(90deg)',
	},
	clearSearchButton: {
		padding: 0,
	},
	container: {
		boxSizing: 'border-box',
		margin: '0 auto',
		maxWidth: 1440,
		width: '100%',
	},
	description: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: 1.5,
		margin: 0,
	},
	emptyRow: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		padding: THEME.spacing.xxl,
		textAlign: 'center',
	},
	hero: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.xl,
	},
	page: {
		backgroundColor: THEME.colors.background.main,
		boxSizing: 'border-box',
		overflowY: 'auto',
		padding: THEME.spacing.xxxl,
		width: '100%',
		[THEME.queries.md]: {
			padding: THEME.spacing.lg,
		},
	},
	panelState: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.md,
		justifyContent: 'space-between',
		lineHeight: 1.5,
		padding: `${THEME.spacing.sm}px 0`,
		[THEME.queries.sm]: {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	searchIcon: {
		color: THEME.colors.primary.main,
		height: 24,
		left: THEME.spacing.lg,
		pointerEvents: 'none',
		position: 'absolute',
		top: '50%',
		transform: 'translateY(-50%)',
		width: 24,
	},
	searchInput: {
		borderColor: THEME.colors.border.main,
		borderRadius: THEME.common.borderRadius + THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.md,
		height: 56,
		padding: `0 ${THEME.spacing.lg}px 0 ${THEME.spacing.xxxl + THEME.spacing.md}px`,
	},
	searchShell: {
		position: 'relative',
	},
	searchStatus: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: 1.5,
		marginTop: THEME.spacing.sm,
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		marginTop: THEME.spacing.xl,
	},
	sectionHeader: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
	},
	sectionTitle: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.08em',
		lineHeight: 1.2,
		margin: 0,
		textTransform: 'uppercase',
	},
	signInButton: {
		flexShrink: 0,
	},
	spinnerState: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		minHeight: 320,
	},
	stateCard: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius + THEME.spacing.xs,
		boxShadow: shadowSm,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		padding: THEME.spacing.xl,
	},
	stateCardActions: {
		display: 'flex',
		justifyContent: 'flex-start',
	},
	stateCardHeader: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	stateCardTitle: {
		color: THEME.colors.text.main,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	subtitle: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.3,
		margin: 0,
	},
	tableCard: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		overflow: 'hidden',
	},
	// The expanded users list owns its own padding via `usersPanel`, so the cell carries none.
	expandedCell: {
		padding: 0,
	},
	usersColumn: {
		textAlign: 'right',
		width: 140,
	},
	usersCountCell: {
		textAlign: 'right',
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',
	},
	title: {
		color: THEME.colors.text.main,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xxl,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.1,
		margin: 0,
	},
	userAvatar: {
		alignItems: 'center',
		backgroundColor: THEME.colors.pill.gray.background,
		borderRadius: '50%',
		color: THEME.colors.pill.gray.icon,
		display: 'flex',
		flexShrink: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		height: 34,
		justifyContent: 'center',
		width: 34,
	},
	userEmail: {
		...THEME.common.truncate,
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.4,
	},
	userName: {
		...THEME.common.truncate,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.4,
	},
	userRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.sm}px 0`,
		[THEME.queries.sm]: {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	userSummary: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		minWidth: 0,
	},
	usersPanel: {
		backgroundColor: THEME.colors.background.main,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.lg}px ${THEME.spacing.md}px ${THEME.spacing.xxxl + THEME.spacing.xs}px`,
	},
});
